/****************************************************
 * Module Name:  tallyBox.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useRef, useState } from "react";
import Constants from "../../utility/constants";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Checkbox, Container, IconButton, Menu, MenuItem, Tooltip, Typography, Stack } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import StoneDetailModal from "../common/stoneDetailModal";
import { alpha, styled } from "@mui/material/styles";
import NoPermission from "../common/noPermission";

import TabMenuSnackbar from "../common/tabMenuSnackbar";
import CommonStoneSelection from "../common/commonStoneSelection";
import Select from "react-select";
import BoxProxy from "../../utility/proxy/manager/boxProxy";
import MoveToBoxModal from "./moveToBoxModal";
import MoveUpIcon from '@mui/icons-material/MoveUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { VscRunAll } from "react-icons/vsc";
import { IoRemoveCircle } from "react-icons/io5";
import { LoadingButton } from "@mui/lab";
import StockTallyProxy from "../../utility/proxy/manager/stockTallyProxy";
import SendAndArchiveIcon from '@mui/icons-material/SendAndArchive';
import colourchart from '../../images/ColorScheme.png'
import { HiPrinter } from "react-icons/hi2";
import PDFModal from "../common/pdfModal";
import ReportProxy from "../../utility/proxy/manager/reportProxy";
import StickerPrintModal from "../common/stickerPrintModal";
import { BsBucketFill } from "react-icons/bs";
import LabTestProxy from "../../utility/proxy/manager/labTestProxy";
import MixStoneBucketModal from "./mixStoneBucketModal";
import StoneMediaModal from "../common/stoneMediaModal";
import VideoLibraryTwoToneIcon from '@mui/icons-material/VideoLibraryTwoTone';
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import { Modal } from "react-bootstrap";
import logo from '../../images/pansuriya.jpg'
import RecommendIcon from '@mui/icons-material/Recommend';
import PacketHistoryModal from "../common/packetHistoryModal";
import PageTitle from "../common/pageTitle";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import StockTally from "../report/stocktally";
import { GoInbox } from "react-icons/go";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    "& .super-app-theme--AVAILABLE": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--MEMO_REQUESTED": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--HOLD": {
        backgroundColor: "#b6b9d6",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        color: "#000",
        "&:hover": {
            backgroundColor: "#b6b9d6",
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--SELL": {
        backgroundColor: "#f5d7d7",
        color: "#080808",
        "&:hover": {
            backgroundColor: "#f5d7d7",
            color: "#080808",
        },
    },
    "& .super-app-theme--AVAILABLE_IN_TRANSIT": {
        backgroundColor: "#FEBD7D",
        color: "#080808",
        "&:hover": {
            backgroundColor: "#FEBD7D",
            color: "#080808",
        },
    },
    "& .super-app-theme--SELL_IN_TRANSIT": {
        backgroundColor: "#ffc247",
        color: "#080808",
        "&:hover": {
            backgroundColor: "#ffc247",
            color: "#080808",
        },
    },
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        backgroundColor: 'grey',
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },

}));

const StyledMenu = styled((props) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color:
            theme.palette.mode === 'light'
                ? 'rgb(55, 65, 81)'
                : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity
                ),
            },
        },
    },
}));

const TallyBox = () => {

    const [stockData, setStockData] = useState([]);
    const [stockLoader, setStockLoader] = useState(false);
    const [moveLoader, setMoveLoader] = useState(false);
    const [tallyLoader, setTallyLoader] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [stoneId, setStoneId] = useState("");
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [successSnackbar, setSuccessSnackbar] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [noOfSelectedStones, setNoOfSelectedStones] = useState("0");
    const [selectedTotalSize, setSelectedTotalSize] = useState("0");
    const [selectedAvgDiscount, setSelectedAvgDiscount] = useState("0");
    const [selectedavgPrice, setSelectedavgPrice] = useState("0");
    const [selectedtotalPrice, setSelectedtotalPrice] = useState("0");
    const [sortModel, setSortModel] = React.useState([]);
    const [columnNames, setColumnNames] = React.useState([]);
    const [isCheckBoxSelected, setCheckBoxSelected] = useState(false);
    const [isDropdownSelected, setDropDownSelected] = useState(true);
    const [showMoveToBoxModal, setShowMoveToBoxModal] = useState(false);
    const [isNotifyDisabled, setIsNotifyDisabled] = useState(true);
    const [isStartTallyDisabled, setIsStartTallyDisabled] = useState(false);
    const [boxSelectData, setBoxSelectData] = useState({
        box: "",
        boxOptions: [],
    });
    const [scanRfLoad, setScanRfLoad] = useState(false);
    const [extraPacket, setExtraPacket] = useState([]);
    const [showselectedSnackbar, setshowselectedSnackbar] = useState(false);
    const [unSelectionModel, setUnSelectionModel] = useState([]);
    const [tagsNotFound, setTagsNotFound] = useState([]);
    const [noTagsNotFound, setNoTagsNotFound] = useState([]);
    const [noStoneFound, setNoStoneFound] = useState([]);
    const [byte, setByte] = useState();
    const [showStickerModal, setShowStickerModal] = useState(false);

    const [showMixBucketModal, setShowMixBucketModal] = useState(false);
    const [bucketData, setBucketData] = useState([]);
    const [warning, setWarning] = useState(false);
    const [warningmessage, setWarningMessage] = useState(false);

    const [stoneMediaURL, setStoneMediaURL] = useState([]);
    const [showStoneMediaModal, setShowStoneMediaModal] = useState(false);
    const [kapanId, setKapanId] = useState("");
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [showRemoveFromBoxModal, setShowRemoveFromBoxModal] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
    const [showBox, setShowBox] = useState(false);
    const [showWithoutBox, setShowWithoutBox] = useState(false);

    const extraPacketRef = useRef(extraPacket);

    useEffect(() => {
        extraPacketRef.current = extraPacket;
    }, [extraPacket]);

    // eslint-disable-next-line
    const [stockTallyPermission, setStockTallyPermission] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.OPERATION_STOCKTALLY
        )
    );

    // Create a connection
    const ws = useRef(null);

    const handleSearch = () => {

        if (!boxSelectData.box && !isCheckBoxSelected) {
            return;
        }
        setStockLoader(true);
        let userLocationId = Constants.loggedInUser.locationid;
        let data = {
            status: ["AVAILABLE", "MEMO_REQUESTED", "HOLD", "SELL", "OFFLINE"],
            boxids: isCheckBoxSelected ? [] : [boxSelectData.box.value],
        }
        if (isCheckBoxSelected) {
            BoxProxy.GetAllStoneWithoutBoxByLocationId(userLocationId, data, (response) => {
                if (response) {
                    response = response.sort((a, b) => {
                        const statusOrder = ["SELL", "HOLD", "MEMO_REQUESTED", "AVAILABLE", "OFFLINE"];
                        return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
                    });

                    const uniqueFilterValues = {
                        labshape: Utility.getDistinctOptions(response, 'labshape'),
                        customer: Utility.getDistinctOptions(response, "customerid"),
                    }

                    const combinedFilterValues = {
                        customer: uniqueFilterValues.customer.map(value => {
                            const matchingItem = response.find(item => item.customerid === value);
                            return matchingItem ? `${value} | ${matchingItem.customercompany}` : [];
                        }),
                        labshape: uniqueFilterValues.labshape,
                    }
                    prepareColumns(combinedFilterValues);
                    setStockData(response);
                    setStockLoader(false);
                    loadTotalData(response);
                } else {
                    setStockLoader(false);
                    setErrorSnackbar(true);
                }
            })

        } else {
            BoxProxy.GetAllStoneInBoxByLocationId(userLocationId, data, (response) => {
                if (response) {
                    response = response.sort((a, b) => {
                        const statusOrder = ["SELL", "HOLD", "MEMO_REQUESTED", "AVAILABLE"];
                        return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
                    });

                    const uniqueFilterValues = {
                        labshape: Utility.getDistinctOptions(response, 'labshape'),
                        customer: Utility.getDistinctOptions(response, "customerid"),
                    }
                    const combinedFilterValues = {
                        customer: uniqueFilterValues.customer.map(value => {
                            const matchingItem = response.find(item => item.customerid === value);
                            return matchingItem ? `${value} | ${matchingItem.customercompany}` : [];
                        }),
                        labshape: uniqueFilterValues.labshape,
                    }
                    prepareColumns(combinedFilterValues);
                    setStockData(response);
                    setStockLoader(false);
                    if (selectionModel.length <= 0)
                        loadTotalData(response);
                } else {
                    setStockLoader(false);
                    setErrorSnackbar(true);
                }
            })
        }
    };

    useEffect(() => {
        PageTitle("OPERATION_STOCKTALLY_BOX");
        getAllBoxSelectData();
    }, []);

    useEffect(() => {
        if (boxSelectData) {
            setIsStartTallyDisabled(false);
            handleSearch();
        }
    }, [boxSelectData]);

    useEffect(() => {
        handleSearch();
    }, [isCheckBoxSelected]);

    useEffect(() => {
        if (selectionModel.length <= 0) {
            loadTotalData(stockData);
            return;
        }
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size
        stockData.forEach((iData) => {
            if (selectionModel.includes(iData.stoneid)) {
                let pricePerCarat = iData.price
                totalSize += iData.size;
                totalPrice += (iData.size * pricePerCarat);
                total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));
            }
        });
        setNoOfSelectedStones(selectionModel.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );

    }, [selectionModel]);

    const handleClose = () => {
        setShowStoneDetailModal(false);
    };

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const handlePacketDetailModal = (params, kapanid) => {
        setPacketId(params);
        setKapanId(kapanid);
        setShowPacketDetailModal(true);
    };

    const getAllBoxSelectData = () => {
        BoxProxy.GetAllBoxesByLocation([Constants.loggedInUser.locationid], (response) => {
            setData(response);
        });
    };

    const getAllStonesWithoutBox = () => {
        setCheckBoxSelected(prevState => {
            const newState = !prevState;
            return newState;
        });
        setDropDownSelected(!isDropdownSelected);
        setIsStartTallyDisabled(!isStartTallyDisabled);
    };

    const handleSave = (boxid, stoneIds) => {
        setMoveLoader(true);
        BoxProxy.UpdateBoxIdForStone(boxid, stoneIds, (responseData) => {
            if (responseData) {
                setShowRemoveFromBoxModal(false);
                setSuccessSnackbar(true);
                setSuccessMessage(responseData.errorMessage);
                setSnackbarVariant("success");
                handleSearch();
                setSelectionModel([]);
                setMoveLoader(false);
                setShowMoveToBoxModal(false);
            } else {
                setShowRemoveFromBoxModal(false);
                setErrorSnackbar(true);
                setMoveLoader(false);
            }
        });
    };

    const boxOptionsData = () => {
        if (isDropdownSelected) {
            let boxData = boxSelectData.boxOptions.filter((item) => item.value !== boxSelectData.box.value);
            let boxDataOptions = {
                box: boxData.length > 0
                    ? boxData[0]
                    : null,
                boxOptions: boxData
            }
            return boxDataOptions;
        } else {
            return boxSelectData
        }
    }

    const setData = (response) => {
        let optionsArray = [];
        response.map((data) => {
            let option = { value: data.id, label: data.boxname ? data.id + " | " + data.boxname : data.id };
            optionsArray.push(option);
            return true;
        });
        setBoxSelectData({
            ...boxSelectData,
            boxOptions: optionsArray,
            box:
                boxSelectData.box === "" && optionsArray.length > 0
                    ? optionsArray[0]
                    : boxSelectData.box,
        });
    };

    const handleSelection = (data) => {
        const [tempStockData, tempSelectionModel] = Utility.handleSelection(data, stockData, selectionModel);

        setStockData(tempStockData);
        setSelectionModel(tempSelectionModel);
    }

    const setTagsSelection = (tags) => {
        //tags is array or string which are tag sample ['t1','t2']
        let lStockData = stockData;
        let iNewSelected = 0;
        let iUnSelectedTags = 0;
        let selectedCheckBox = [];
        let unSelectedCheckBox = [];
        let tagsFound = [];
        let tagsNotFound = [];

        // tags = tags.map(tag => tag.replace(/^\['(.+?)'\]$/, '$1'));

        for (let loopCnt = 0; loopCnt < lStockData.length; loopCnt++) {
            if (lStockData[loopCnt].tag && tags.has(lStockData[loopCnt].tag)) {
                selectedCheckBox.push(lStockData[loopCnt].stoneid);
                tagsFound.push(lStockData[loopCnt].tag);
                iNewSelected++;
            } else {
                unSelectedCheckBox.push(lStockData[loopCnt].stoneid);
            }
        }

        tagsNotFound = [...tags].filter(tag => !tagsFound.includes(tag));
        iUnSelectedTags = tags.size - iNewSelected;

        setUnSelectionModel(unSelectedCheckBox);
        setTagsNotFound(tagsNotFound);
        setNoTagsNotFound(iUnSelectedTags);
        setNoStoneFound(iNewSelected);
        setStockData(() => {
            let arr = [];
            let checkedArr = [];
            lStockData.map((res) => {
                let option = {
                    ...res,
                    isChecked: selectedCheckBox.includes(res.stoneid) ? "true" : "false",
                };
                if (option.isChecked === "true") {
                    checkedArr.push(option);
                } else {
                    arr.push(option);
                }
                return true;
            });
            return [...arr, ...checkedArr];
        });
        setSelectionModel(prev => Array.from(new Set([...prev, ...selectedCheckBox])));

        if (tagsNotFound.length > 0) {
            setExtraPacket(prev => Array.from(new Set([...prev, ...tagsNotFound])));
        }

        return {
            NewSelectedStones: iNewSelected,
            UnusedTags: iUnSelectedTags,
            tagsNotFound: tagsNotFound,
            selectionModel: Array.from(new Set([...selectionModel, ...selectedCheckBox]))
        };
    };

    const prepareColumns = (uniqueFilterValues) => {
        let columnNames = [];

        Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
            if (columnsData.hasOwnProperty(iColPermission)) {
                const columnData = columnsData[iColPermission];
                columnNames.push(columnData);
                if (columnData.field === "stoneid") {
                    columnNames.push({
                        field: "suratid",
                        headerName: "SURATID",
                        headerAlign: "center",
                        align: "center",
                        width: 80,
                        renderCell: renderCellExpand,
                    })
                } else if (columnData.field === "shape") {
                    columnNames.push({
                        field: "labshape",
                        headerName: "LAB SHAPE",
                        headerAlign: "center",
                        align: "center",
                        width: 90,
                        renderCell: renderCellExpand,
                        type: "singleSelect",
                        valueOptions: uniqueFilterValues.labshape,
                    })
                } else if (columnData.field === "status") {
                    columnNames.push({
                        field: "customercompany",
                        headerName: "CUSTOMER COMPANY",
                        headerAlign: "center",
                        align: "center",
                        width: 160,
                        type: "singleSelect",
                        valueOptions: uniqueFilterValues.customer,
                        filterOperators: Utility.combinedFilters(),
                        renderCell: (params) => (params.row.customerid && params.row.customercompany) ? params.row.customerid + " | " + params.row.customercompany : " - ",
                        valueGetter: (params) => {
                            return (params.row.customerid && params.row.customercompany) ? params.row.customerid + " | " + params.row.customercompany : " - ";
                        },
                    })
                }
            }
        });
        setColumnNames(columnNames);
    }

    const startTally = () => {

        if (!scanRfLoad && !ws.current) {
            ws.current = new WebSocket(Constants.RFID_API);
        }

        // If true that means we need to run stop_scan
        // Open WebSocket connection and start scanning
        if (scanRfLoad) {
            setScanRfLoad(() => !scanRfLoad);
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                ws.current.close();
                ws.current.onclose = () => {
                    getStoneIdByTagId();
                }
            }

            return;
        } else {
            if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
                // WebSocket doesn't exist or was closed — create a new one
                ws.current = new WebSocket(Constants.RFID_API);
                ws.current.onopen = () => {
                    ws.current.send("START_SCAN");
                    setScanRfLoad(prev => !prev);
                };
            } else if (ws.current.readyState === WebSocket.CONNECTING) {
                // It's still connecting — safely assign onopen
                ws.current.onopen = () => {
                    ws.current.send("START_SCAN");
                    setScanRfLoad(prev => !prev);
                };
            } else if (ws.current.readyState === WebSocket.OPEN) {
                // Already open, just send
                ws.current.send("START_SCAN");
                setScanRfLoad(prev => !prev);
            }
        }

        // Listen for incoming messages from WebSocket
        let lstTags = new Set();
        let selectionModel = [];
        ws.current.onmessage = (e) => {
            let message = typeof e.data === 'string' ? e.data : String(e.data);
            if (message.includes("TAG: ")) {
                const parts = message.split("TAG: ");
                if (parts.length === 2) {
                    const remaining = parts[1].trim(); // remove extra whitespace
                    remaining.split(',').forEach(str => lstTags.add(str.trim())); // split and trim each item
                }

                if (stockData.length > 0 && lstTags.size > 0) {
                    let retVal = setTagsSelection(lstTags);
                    selectionModel = retVal.selectionModel;
                }
            } else if (message.includes("SCAN_COMPLETED")) {
                if (selectionModel.length < rows.length) {
                    ws.current.send("START_SCAN");
                } else {
                    ws.current.close();
                    ws.current.onclose = () => {
                        getStoneIdByTagId();
                    }
                    setScanRfLoad(prev => !prev);
                }
            }
        };
        setIsNotifyDisabled(false);
    }

    const notifyUser = () => {
        setTallyLoader(true);
        let data = {
            boxid: boxSelectData.box.value,
            locationid: Constants.loggedInUser.locationid,
            userid: Constants.loggedInUser.id,
            totalstone: stockData.length,
            nostonefound: noStoneFound,
            stonesnotfound: unSelectionModel.join(', '),
            notagnotfound: noTagsNotFound,
            tagsnotfound: tagsNotFound.join(', ')
        }

        StockTallyProxy.sendTallyNotification(data, (response) => {
            if (response && response.status === "Success") {
                if (scanRfLoad) {
                    setScanRfLoad(() => !scanRfLoad);
                    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                        ws.current.close();
                        ws.current.onclose = () => {
                            getStoneIdByTagId();
                        }
                    }
                } else {
                    setSuccessSnackbar(true);
                    setSuccessMessage(response.errorMessage);
                    setSnackbarVariant("success");
                }
                handleSearch();
                setSelectionModel([]);
                setTallyLoader(false);
            } else {
                setErrorSnackbar(true);
                setTallyLoader(false);
                if (scanRfLoad) {
                    setScanRfLoad(() => !scanRfLoad);
                    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                        ws.current.close();
                        ws.current = null;
                    }
                }
            }
        })

        setIsNotifyDisabled(true);
        setIsStartTallyDisabled(false);
    }

    const loadTotalData = (data) => {
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size

        data.forEach((iData) => {
            let pricePerCarat = iData.price
            totalSize += iData.size;
            totalPrice += (iData.size * pricePerCarat);
            total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));

        });
        setNoOfSelectedStones(data.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
    };

    const handleMixBucketStoneSave = (data) => {
        setMoveLoader(true);
        LabTestProxy.AddStonesToMix(data, (responseData) => {
            if (responseData && responseData.status === "Success") {
                handleSearch();
                setShowMixBucketModal(false);
                setSuccessSnackbar(true);
                setSuccessMessage("Stone sent to Mix");
                setSnackbarVariant("success");
                setMoveLoader(false);
            } else {
                setShowMixBucketModal(false);
                setSuccessSnackbar(true);
                setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
                setMoveLoader(false);
            }
        });
    }

    const setTagsSelections = (tags) => {
        //tags is array or string which are tag sample ['t1','t2']
        let lStockData = stockData;
        let iNewSelected = 0;
        let iUnSelectedTags = 0;
        let selectedCheckBox = [];
        let tagsFound = [];
        let tagsNotFound = [];

        for (let loopCnt = 0; loopCnt < lStockData.length; loopCnt++) {
            if (lStockData[loopCnt].tag && tags.has(lStockData[loopCnt].tag)) {
                selectedCheckBox.push(lStockData[loopCnt].stoneid);
                tagsFound.push(lStockData[loopCnt].tag);
                iNewSelected++;
            }
        }

        tagsNotFound = [...tags].filter(tag => !tagsFound.includes(tag));
        iUnSelectedTags = tags.size - iNewSelected;

        setStockData(() => {
            let selectedStone = [];
            let unSelectedStone = [];
            lStockData.forEach((res) => {
                if (selectedCheckBox.includes(res.stoneid)) {
                    selectedStone.push(res);
                } else {
                    unSelectedStone.push(res);
                }
            });
            return [...selectedStone, ...unSelectedStone];
        });
        setSelectionModel(selectedCheckBox);
        if (tagsNotFound.length > 0) {
            setExtraPacket(prev => Array.from(new Set([...prev, ...tagsNotFound])));
        }

        return {
            NewSelectedStones: iNewSelected,
            UnusedTags: iUnSelectedTags,
            tagsNotFound: tagsNotFound,
        };
    };

    const startScanning = () => {

        if (!ws.current) {
            ws.current = new WebSocket(Constants.RFID_API);
        }

        // Open WebSocket connection and start scanning
        if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
            // WebSocket doesn't exist or was closed — create new one
            ws.current = new WebSocket(Constants.RFID_API);
            ws.current.onopen = () => {
                ws.current.send("START_SCAN");
            };
        } else if (ws.current.readyState === WebSocket.CONNECTING) {
            // Still connecting — safely attach onopen
            ws.current.onopen = () => {
                ws.current.send("START_SCAN");
            };
        } else if (ws.current.readyState === WebSocket.OPEN) {
            // Already open — send immediately
            ws.current.send("START_SCAN");
        }

        // Listen for incoming messages from WebSocket
        let lstTags = new Set();
        ws.current.onmessage = (e) => {
            let message = typeof e.data === 'string' ? e.data : String(e.data);
            if (message.includes("TAG: ")) {
                const parts = message.split("TAG: ");
                if (parts.length === 2) {
                    const remaining = parts[1].trim(); // remove extra whitespace
                    remaining.split(',').forEach(str => lstTags.add(str.trim())); // split and trim each item
                }

                if (stockData.length > 0) {
                    setTagsSelections(lstTags);
                }
            } else if (message.includes("SCAN_COMPLETED")) {
                ws.current.close();
                ws.current.onclose = () => {
                    getStoneIdByTagId();
                }
            }
        };
    }

    const getStoneIdByTagId = () => {
        if (extraPacketRef.current.length > 0) {
            StoneProxy.GetStoneIdByTagId(extraPacketRef.current, (response) => {
                if (response) {
                    let resultString = Object.entries(response)
                        .map(([stoneid, tag]) => `${stoneid}: ${tag}`)
                        .join(', ');

                    if (Object.entries(response).length < extraPacketRef.current.length) {
                        const usedTags = new Set(Object.values(response));
                        const extraTags = extraPacketRef.current.filter(tag => !usedTags.has(tag));

                        const extraTagString = extraTags.map(tag => `STONE_ID: ${tag}`).join(', ');
                        resultString = resultString + ', ' + extraTagString;
                    }

                    setSuccessSnackbar(true);
                    setSuccessMessage(`You have ${resultString} extra package on your RF Machine`);
                    setSnackbarVariant("error");
                    setExtraPacket([]);
                    ws.current = null;
                } else {
                    setSuccessSnackbar(true);
                    setSuccessMessage(Constants.APiFailErrorMessage);
                    setSnackbarVariant("error");
                    ws.current = null;
                    setExtraPacket([]);
                }
            })
        }
    }

    const columnsData = {
        "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
            field: "stoneid",
            headerName: "STONEID",
            pinning: { position: 'left' },
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            cellClassName: 'stickyColumn',
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "24ba0bf8-4ca1-4726-ad90-19a62c8c72e6": {
            field: "boxid",
            headerName: "BOXID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 80
        },
        "cf694b75-ba5d-4a73-8fcf-e7566974c4b6": {
            field: "media",
            headerName: "MEDIA",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: (params) => (
                <Tooltip title={"Stone Media"} sx={{ cursor: "pointer" }}>
                    <VideoLibraryTwoToneIcon
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                            setStoneMediaURL(params.row.stonemediaurl);
                            setShowStoneMediaModal(true);
                        }} />
                </Tooltip>
            ),
        },
        "9dfe5dd6-726c-465b-9f61-22fca6660b4f": {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        "8a492e00-6167-45de-b078-e572e47c8a39": {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handlePacketDetailModal(params.value, params.row.kapanid)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: ["SELL", "HOLD", "MEMO_REQUESTED", "AVAILABLE"],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => (params.value === "MEMO_REQUESTED" ? "AVAILABLE" : params.value),
            renderCell: (params) => (params.value === "MEMO_REQUESTED" ? "AVAILABLE" : params.value),
            cellClassName: (params) => {
                return params.row.stoneoldstatus === "AVAILABLE" ?
                    "super-app-theme--AVAILABLE_IN_TRANSIT" : (params.row.stoneoldstatus === "SELL" ? "super-app-theme--SELL_IN_TRANSIT"
                        : `super-app-theme--${params.row.status.toUpperCase()}`)
            },
        },
        "54fbd914-6ce7-44a8-9354-db6125ba1992": {
            field: "tag",
            headerName: "TAG",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: (params) => (!params.value) ? "" : params.value,
        },
        "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => Constants.STONE_TYPE[params.value],
            renderCell: (params) => Constants.STONE_TYPE[params.value],
        },
        "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            width: 40,
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            width: 65,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "d4992de9-92ab-4f9e-ba12-6c746e935611": {
            field: "colordescriptions",
            headerName: "COLOR DESCRIPTIONS",
            headerAlign: "center",
            align: "center",
            width: 160,
            renderCell: renderCellExpand,
        },
        "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "3597803f-8932-40c6-8de6-81006f4d6d5b": {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "5541c182-21b8-496e-b342-b0b89f3d302f": {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "8296e627-0be5-4a66-830f-ea6867cea137": {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "798a8663-0f0c-41be-8512-16dd60d5f25e": {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "right",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
        },
        "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
            field: "rapnetprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "right",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.row.rapnetprice
        },
        "39c634f1-2644-4307-acbb-983b4de4229e": {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        "13d48508-b8c9-4661-8a6c-9fb22a3408a3": {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        "567202e6-c31c-4c19-9998-1cacae05af6e": {
            field: "depth",
            headerName: "DEPTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "19ce7030-402c-495c-84ac-a29093bdb0ac": {
            field: "locationid",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "ab3e506e-bddb-456d-b967-922dcee74d56": {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "f97a0118-3891-4f77-abf7-8636c26d594c": {
            field: "country",
            headerName: "COUNTRY",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "cb7e6add-67ba-4194-ac49-fd0d967db4e8": {
            field: "quality",
            headerName: "QUALITY",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        "b718f242-4a82-42e7-b98a-40bbf0f67c21": {
            field: "shade",
            headerName: "SHADE",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: renderCellExpand,
        },
        "01e90aca-bdcb-4e8e-80ec-2d7007df8cd3": {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        "3c62ef48-9538-4df7-9484-a9ae1ba31086": {
            field: "height",
            headerName: "HEIGHT",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "eed9b7eb-17e4-4ec6-9c9d-c299f885544c": {
            field: "width",
            headerName: "WIDTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "8eaa8a03-d163-436d-8912-51ea06306f44": {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        "77e48fa7-d118-4ab8-865b-9b85b32a004f": {
            field: "fancycolour",
            headerName: "F.COLOR",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "ccba9873-174e-4060-963a-9893c3625bb2": {
            field: "fancycolorovertone",
            headerName: "OVERTONE",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        "c9aa6ee7-30e3-423e-a534-80bb5ef7bbaf": {
            field: "fancycolorintensity",
            headerName: "F.COL.INTENSITY",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        "706a5cfb-e196-43d1-bbe3-c52aad9a46c1": {
            field: "comment",
            headerName: "COMMENT",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "e7210f4a-6e0a-4591-8428-e6845004ed04": {
            field: "crownheight",
            headerName: "CR.HEIGHT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "c4e65984-1682-4c1e-812b-8938e9078b2d": {
            field: "crownangle",
            headerName: "CR.ANG",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "0f1de38d-3ae3-4209-a4d5-afe9f3018f22": {
            field: "culetsize",
            headerName: "CULET SZ",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "e3f77521-2c57-46bc-a02b-3c884685d2be": {
            field: "culetcondition",
            headerName: "CULET",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "e5c1f34e-6e1c-4ee8-bb8e-3236716fd1ff": {
            field: "depthpercent",
            headerName: "DEPTH%",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "e83c2978-9088-49f0-9dc1-bcced73025fd": {
            field: "gridlemin",
            headerName: "GIRDLEMIN",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "cee57bac-9c19-40d5-b51a-51ed913dbf73": {
            field: "intensitylongtitle",
            headerName: "INTENSITYLONGTITLE",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "06d2a589-0d42-4dde-8724-acf2448c839a": {
            field: "laserinscription",
            headerName: "INS",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
            cellClassName: (params) => Utility.highlightLasertInscriptionCell(params.value)
        },
        "dd934505-1865-48cb-878f-f5d3c18af2d5": {
            field: "note",
            headerName: "NOTE",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "61f8a5f2-8516-419a-b6fd-39ef109cc2f4": {
            field: "paviliondepth",
            headerName: "PV.DEPTH",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "70498a64-1f5f-4f03-8910-a6d7851e4aba": {
            field: "tablepercent",
            headerName: "TABLE%",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "db078198-4416-4d53-b3d0-70d3a5418f65": {
            field: "open",
            headerName: "OPEN",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "e9028170-e978-4978-a2c9-1889c1821495": {
            field: "gridlecondition",
            headerName: "GIRDLE COND",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "fe8f55f3-358a-4418-bcdf-c3da4b01db67": {
            field: "gridlepercent",
            headerName: "GIRDLE%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "daf69bba-bb1f-4d54-9dc7-9383ad429585": {
            field: "pavilionangle",
            headerName: "PV.ANGLE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "ecfdfc0c-6a25-4a6d-9c8f-3406c23410af": {
            field: "white",
            headerName: "WHITE",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "7e356515-7cc5-4cd4-a1a4-a1b909b3b4e3": {
            field: "avgrapnetprice",
            headerName: "AVG RAP PRICE",
            headerAlign: "center",
            align: "center",
            width: 140,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        "a330b2b8-b9e4-40bb-bb7a-d0e83a717a74": {
            field: "avgdiscount",
            headerName: "AVG DIS",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        "1ac589a9-ff76-4b9a-9b6e-f0d7951dc0ca": {
            field: "avgprice",
            headerName: "AVG PRICE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        "49b8f961-fe52-48ca-afbe-ceab5d7dc740": {
            field: "avgtotal",
            headerName: "AVG TOTAL",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        "e32fe363-07cc-4307-82d2-82ae1069b5be": {
            field: "age",
            headerName: "AGE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueGetter: (params) => {
                if (params.value)
                    return Utility.ConvertDateToAge(params.value);
                else
                    return null;
            },
            renderCell: (params) => {
                return params.value;
            },
        },
        "f3223657-c8d2-449c-b2f5-5a54c78401ce": {
            field: "tableinc",
            headerName: "TABLE INC",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "f114ad2c-8560-4da7-9b03-03ba6bfe39a5": {
            field: "sideinc",
            headerName: "SIDE INC",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "e993a4f7-7c50-4d83-abae-fccc03382742": {
            field: "h_and_a",
            headerName: "H&A",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "f2fe7666-6489-40ce-b4b7-040c68c88bf3": {
            field: "eyeclean",
            headerName: "EYE CLEAN",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "b38b9fdf-bfb8-43c6-82a2-1e366fa71523": {
            field: "tinge",
            headerName: "TINGE",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.tinge.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 120,
        },
        "c95089ee-166d-4783-817c-2638f6d40df1": {
            field: "milky",
            headerName: "MILKY",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.milky.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 120,
        },
        "2a6475ff-481f-48c6-9e92-3ede0b97d7c0": {
            field: "luster",
            headerName: "LUSTER",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "556ae429-3605-4df4-88d8-b5223fc2e2fb": {
            field: "tableblack",
            headerName: "TABLE BLACK",
            headerAlign: "center",
            align: "center",
            type: "boolean",
            width: 120,
        },
        "5c70d1ca-2a7b-4660-8160-0beff7ad1150": {
            field: "sideblack",
            headerName: "SIDE BLACK",
            headerAlign: "center",
            align: "center",
            type: "boolean",
            width: 120,
        },
        "1efc63c7-1b3d-4fb9-a52e-ccdf6bfb6b9b": {
            field: "roughorigin",
            headerName: "ROUGH ORIGIN",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "af5eb764-747f-458c-947f-16ec8b4e5536": {
            field: "countryoforigin",
            headerName: "COUNTRY OF ORIGIN",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
    };

    const rows = stockData;
    const columns = columnNames;

    const allowed = Utility.isReadAllowed(stockTallyPermission);

    // Color Scheme Drop Down Button
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleCloseButton = () => {
        setAnchorEl(null);
    };

    // Packet Print
    const printPacket = () => {
        let data = stockData.filter(item => selectionModel.includes(item.stoneid));
        ReportProxy.GeneratePacketPrintPDF(data, response => {
            if (response) {
                setByte(response.fileBytes);
                openModal()
            }
        })
    }

    const [isModalOpen, setIsModalOpen] = useState(false);

    const openModal = () => {
        setIsModalOpen(true);
    };
    const closeModal = () => {
        setIsModalOpen(false);
    };
    const handleCloseShowBoxModal = () => {
        setShowBox(false);
    };

    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '32px',
            height: '32px',
            fontSize: '13px',
            minWidth: '120px',
        }),
        valueContainer: (base) => ({
            ...base,
            height: '32px',
            padding: '0 6px',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '32px',
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
    };


    const CustomToolbar = ({ showWithoutBox, setShowWithoutBox }) => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    }
                }}
            >
                <CommonStoneSelection
                    title={"Enter multiple Stone Ids"}
                    handleSelection={handleSelection}
                    bgColor='#454545'
                />
                <Tooltip title={"Start Scan"}>
                    <IconButton
                        color={"success"}
                        onClick={() => startScanning()}
                        disabled={stockData.length === 0}
                    >
                        {<VscRunAll />}
                    </IconButton>
                </Tooltip>

                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                {Utility.isAddAllowed(stockTallyPermission) ? (
                    <Tooltip title={"Print Packet"}>
                        <Button
                            variant="outlined"
                            size="small"
                            component="label"
                            color="secondary"
                            startIcon={<HiPrinter size={14} />}
                            disabled={selectionModel.length === 0}
                            onClick={printPacket}
                        >
                            Packet
                        </Button>
                    </Tooltip>
                ) : null}
                {Utility.isAddAllowed(stockTallyPermission) ? (
                    <Tooltip title={"Print Sticker"}>
                        <Button
                            variant="outlined"
                            size="small"
                            component="label"
                            color="secondary"
                            startIcon={<HiPrinter size={14} />}
                            onClick={() => setShowStickerModal(true)}
                            disabled={stockData.length === 0}
                        >
                            Sticker
                        </Button>
                    </Tooltip>
                ) : null}

                <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />
                <Box >
                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>Box ID </Typography>
                </Box>
                <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    className="basic-single"
                    classNamePrefix="select"
                    value={boxSelectData.box}
                    isSearchable={true}
                    name="Box ID"
                    menuPortalTarget={document.body}
                    styles={customSelectStyles}
                    options={boxSelectData.boxOptions}
                    isDisabled={isCheckBoxSelected}
                    onChange={(selectedOption) => {
                        setBoxSelectData((prevState) => {
                            const updatedData = {
                                ...prevState,
                                box: selectedOption,
                            };
                            return updatedData;
                        });
                    }}
                />
                <Tooltip title={scanRfLoad ? "Stop Tally" : "Start Tally"}>
                    <LoadingButton
                        component="label"
                        size="small"
                        startIcon={<VscRunAll size="18" />}
                        color={scanRfLoad ? "error" : "primary"}
                        variant='outlined'
                        disabled={isStartTallyDisabled}
                        onClick={() => startTally()}
                    >
                        {scanRfLoad ? "Stop Tally" : "Start Tally"}
                    </LoadingButton>
                </Tooltip>
                <Tooltip title="Notify">
                    <LoadingButton
                        component="label"
                        size="small"
                        startIcon={<SendAndArchiveIcon size={18} />}
                        color='success'
                        variant='outlined'
                        disabled={isNotifyDisabled}
                        loading={tallyLoader}
                        loadingPosition="start"
                        onClick={() => notifyUser()}
                    >
                        Tally Submit
                    </LoadingButton>
                </Tooltip>
                <Checkbox
                    checked={showWithoutBox}
                    onChange={(e) => {
                        setShowWithoutBox(e.target.checked);
                        getAllStonesWithoutBox();
                    }}
                    size="small"
                />
                <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>Stones Without Box</Typography>
                <Button
                    component="label"
                    size="small"
                    startIcon={<MoveUpIcon size="large" />}
                    color='secondary'
                    variant='outlined'
                    onClick={() => setShowMoveToBoxModal(true)}
                    disabled={selectionModel.length === 0 || stockLoader}
                >
                    Move To Box
                </Button>
                {stockLoader ? (
                    <CircularProgress sx={{ marginLeft: "7px", cursor: "pointer", width: "1.7rem !important" }} />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => handleSearch()}
                            color="primary"
                            fontSize="large"
                            sx={{ cursor: "pointer", width: "1.7rem" }}
                        />
                    </Tooltip>
                )}

                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }

    return (
        !allowed ? <NoPermission /> :
            <div>
                <Container maxWidth="xxl">

                    {/*need to send pdf in byte string to modal */}
                    <PDFModal show={isModalOpen} onHide={closeModal} pdfByteString={byte} pdfName={"packetPrint"} />

                    {showStoneDetailModal ? (
                        <StoneDetailModal
                            show={showStoneDetailModal}
                            stoneId={stoneId}
                            handleClose={handleClose}
                        />
                    ) : null}

                    {showStickerModal ? (
                        <StickerPrintModal
                            show={showStickerModal}
                            stoneIds={selectionModel}
                            handleClose={() => setShowStickerModal(false)}
                        />
                    ) : null}

                    {showStoneMediaModal ? (
                        <>
                            <StoneMediaModal
                                show={showStoneMediaModal}
                                stoneMediaURL={stoneMediaURL}
                                handleClose={() => setShowStoneMediaModal(false)}
                            />
                        </>
                    ) : null}

                    {showKapanDetailModal ? (
                        <KapanHistoryModal
                            show={showKapanDetailModal}
                            kapanId={kapanId}
                            close={() => setShowKapanDetailModal(false)}
                        />
                    ) : null}
                    {showPacketDetailModal ? (
                        <PacketHistoryModal
                            show={showPacketDetailModal}
                            packetId={packetId}
                            kapanId={kapanId}
                            close={() => setShowPacketDetailModal(false)}
                        />
                    ) : null}
                    <TabMenuSnackbar
                        show={successSnackbar}
                        message={successMessage}
                        variant={snackbarVariant}
                        autoHide={false}
                        title={snackbarVariant === "success" ? "Success" : "Failed"}
                        handleClose={() => setSuccessSnackbar(false)}
                    />

                    <TabMenuSnackbar
                        show={errorSnackbar}
                        message={'Error'}
                        variant={"error"}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={"Error"}
                        handleClose={() => setErrorSnackbar(false)}
                    />

                    <TabMenuSnackbar
                        show={showselectedSnackbar}
                        message={<Box> Stones selected successfully</Box>}
                        variant={"success"}
                        autoHide={Constants.SnackBarCloseTimer}
                        handleClose={() => setshowselectedSnackbar(false)}
                        title="Success"
                    />

                    {warning ? (
                        <TabMenuSnackbar
                            show={true}
                            message={warningmessage}
                            variant={"warning"}
                            autoHide={false}
                            title={"Warning"}
                            handleClose={() => setWarning(false)}
                        />
                    ) : null}

                    {showMoveToBoxModal && (
                        <MoveToBoxModal
                            show={showMoveToBoxModal}
                            setShow={setShowMoveToBoxModal}
                            handleSave={handleSave}
                            boxIdoptions={boxOptionsData}
                            stoneIds={selectionModel}
                            loading={moveLoader}
                        />
                    )}

                    {showMixBucketModal && (
                        <MixStoneBucketModal
                            show={showMixBucketModal}
                            setShow={setShowMixBucketModal}
                            handleSave={handleMixBucketStoneSave}
                            sourceData={bucketData}
                            loading={moveLoader}
                        />
                    )}

                    {showBox && (
                        <Modal
                            show={showBox}
                            onHide={handleCloseShowBoxModal}
                            dialogClassName="max-price-modalSize"
                            enforceFocus={false}
                        >
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <img
                                        src={logo}
                                        height="40pt"
                                        width="40pt"
                                        alt={Constants.COMPANY_SITE}
                                    />
                                    &nbsp;&nbsp;
                                    BOX TALLY
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <StockTally
                                    isModal={true}
                                />
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="contained" color="error" onClick={handleCloseShowBoxModal}>
                                    Close
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    )}
                    <Box display={"flex"} margin={"4px 8px"} justifyContent='space-between' marginTop={"1px"}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                flexGrow: 1,
                                flexBasis: 0,
                                overflow: "hidden",
                                color: "black",
                                fontWeight: "normal",
                                px: 1,
                                textAlign: "center",
                                paddingTop: "10px",
                                paddingBottom: "21px",
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={2}
                                useFlexGap
                                flexWrap="wrap"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    justifyContent: "flex-start",
                                }}
                            >
                                <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                                    No of Stones: <b>{noOfSelectedStones}</b>
                                </Typography>
                                <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                                    Total Weight: <b>{selectedTotalSize}</b>
                                </Typography>
                                <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                                    Total Price: <b>{selectedtotalPrice}</b>
                                </Typography>
                                <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                                    Avg Price: <b>{selectedavgPrice}</b>
                                </Typography>
                                <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                                    Avg Discount: <b>{selectedAvgDiscount}</b>
                                </Typography>
                            </Stack>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "15px",
                                alignItems: "center",
                                justifyContent: "flex-start",
                            }}
                        >
                            <Tooltip title="Show Box">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    component="label"
                                    sx={{ ml: "5px", height: "2.5rem !important", }}
                                    startIcon={<GoInbox size={16} />}
                                    color="success"
                                    onClick={() => {
                                        setShowBox(true);
                                    }}>
                                    SHOW TALLY
                                </Button>
                            </Tooltip>

                            <LoadingButton
                                component="label"
                                size="small"
                                color="inherit"
                                sx={{ ml: "5px", height: "2.5rem !important" }}
                                startIcon={<IoRemoveCircle size="22" />}
                                variant='outlined'
                                disabled={selectionModel.length === 0 || stockLoader || isCheckBoxSelected}
                                onClick={() => setShowRemoveFromBoxModal(true)}
                            >
                                Remove from Box
                            </LoadingButton>
                            <Tooltip title="Add To Mix">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    component="label"
                                    sx={{ ml: "5px", height: "2.5rem !important" }}
                                    startIcon={<BsBucketFill size={16} />}
                                    color="success"
                                    disabled={selectionModel.length === 0 || stockLoader}
                                    onClick={() => {
                                        let stoneDataArray = [];
                                        let bucketData = [];
                                        stockData.map(iData => {
                                            if (selectionModel.includes(iData.stoneid)) {
                                                if (iData.status !== "AVAILABLE") {
                                                    stoneDataArray.push(iData.stoneid);
                                                } else {
                                                    iData.mixprice = null;
                                                    iData.mixtotal = null;
                                                    bucketData.push(iData);
                                                }
                                            }
                                            return iData;
                                        })
                                        if (stoneDataArray.length === 0) {
                                            setShowMixBucketModal(true);
                                            setBucketData(bucketData);
                                        } else if (stoneDataArray.length > 0) {
                                            setWarning(true);
                                            setWarningMessage('To perform this action, Please select stones with status "AVAILABLE"');
                                        }
                                    }}>
                                    MIX
                                </Button>
                            </Tooltip>
                            <Box
                                id="demo-customized-button"
                                aria-controls={open ? 'demo-customized-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                variant="outlined"
                                disableElevation
                                onClick={handleClick}
                                endIcon={<KeyboardArrowDownIcon />}
                                sx={{
                                    color: "#454545",
                                    borderColor: "#454545",
                                    borderRadius: "4px",
                                    border: "1px solid black",
                                    padding: "5px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "4rem",
                                }}
                            >
                                <img
                                    src={colourchart}
                                    alt="Color Scheme"
                                    style={{ height: "24px", width: "24px" }}
                                />
                                <KeyboardArrowDownIcon />
                            </Box>
                            <StyledMenu
                                id="demo-customized-menu"
                                MenuListProps={{
                                    'aria-labelledby': 'demo-customized-button',
                                    style: { maxHeight: 250 },
                                }}
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleCloseButton}
                            >
                                <MenuItem onClick={handleCloseButton} disableRipple>
                                    <span style={{ height: "15px", width: "15px", backgroundColor: "#000", borderRadius: 20, margin: 10 }}></span>Available
                                </MenuItem>
                                <MenuItem onClick={handleCloseButton} disableRipple>
                                    <span style={{ height: "15px", width: "15px", backgroundColor: "#b6b9d6", borderRadius: 20, margin: 10 }}></span>Hold
                                </MenuItem>
                                <MenuItem onClick={handleCloseButton} disableRipple>
                                    <span style={{ height: "15px", width: "15px", backgroundColor: "#f5d7d7", borderRadius: 20, margin: 10 }}></span>SELL
                                </MenuItem>
                                <MenuItem onClick={handleCloseButton} disableRipple>
                                    <span style={{ height: "15px", width: "15px", backgroundColor: "#FEBD7D", borderRadius: 20, margin: 10 }}></span>AVAILABLE (IN_TRANSIT)
                                </MenuItem>
                                <MenuItem onClick={handleCloseButton} disableRipple>
                                    <span style={{ height: "15px", width: "15px", backgroundColor: "#ffc247", borderRadius: 20, margin: 10 }}></span>SELL (IN_TRANSIT)
                                </MenuItem>
                            </StyledMenu>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: '69vh',
                        }}
                    >
                        <StyledDataGrid
                            disableRowSelectionOnClick={true}
                            localeText={{ noRowsLabel: "No Record Found" }}
                            sx={{ height: "69vh", fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight, }}
                            slots={{
                                toolbar: CustomToolbar,
                            }}
                            rows={rows}
                            style={{}}
                            sortModel={sortModel}
                            onSortModelChange={(newModel) => setSortModel(newModel)}
                            density="compact"
                            columns={columns}
                            getRowId={(row) => row.stoneid}
                            checkboxSelection={true}
                            onRowSelectionModelChange={(newSelectionModel) => {
                                setSelectionModel(newSelectionModel);
                            }}
                            loading={stockLoader}
                            rowSelectionModel={selectionModel}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        locationid: false,
                                        country: false,
                                        quality: false,
                                        shade: false,
                                        repacode: false,
                                        createdat: false,
                                        height: false,
                                        width: false,
                                        crownheight: false,
                                        crownangle: false,
                                        culetsize: false,
                                        culetcondition: false,
                                        depthpercent: false,
                                        gridlemin: false,
                                        intensitylongtitle: false,
                                        note: false,
                                        paviliondepth: false,
                                        gridlecondition: false,
                                        gridlepercent: false,
                                        pavilionangle: false,
                                        white: false,

                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showWithoutBox,
                                    setShowWithoutBox,
                                },
                            }}
                        />
                    </Box>

                    {showRemoveFromBoxModal ? (
                        <Modal
                            show={true}
                            onHide={() => setShowRemoveFromBoxModal(false)}
                            backdrop="static"
                            keyboard={true}
                            centered
                            enforceFocus={false}
                            dialogClassName="confirmationModal"
                        >
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <img
                                        src={logo}
                                        height="40pt"
                                        width="40pt"
                                        alt={Constants.COMPANY_SITE}
                                    />
                                    &nbsp;&nbsp;
                                    Remove from Box
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Typography variant="h6" sx={{ textAlign: "center" }}>
                                    Are you sure you want to remove the stones from the Box?
                                </Typography>
                            </Modal.Body>
                            <Modal.Footer>
                                <LoadingButton
                                    variant="contained"
                                    onClick={() => handleSave(-1, selectionModel)}
                                    loading={moveLoader}
                                    loadingPosition="start"
                                    startIcon={<RecommendIcon />}
                                    type="Submit"
                                    sx={{ mr: 3 }}
                                >
                                    Confirm
                                </LoadingButton>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => setShowRemoveFromBoxModal(false)}
                                    sx={{ mr: 2 }}
                                    disabled={moveLoader}
                                >
                                    Cancel
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    ) : null}
                </Container>
            </div >
    );
}

export default TallyBox;
